import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { ConversationMessages } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import { useAppDispatch } from '../../../../data/store'
import messagesApi, { useGetLatestMessageQuery } from '../messagesApi'
import { getConversationsClient } from '../messagesSelectors'
import { MessengerParticipant, MessengerThread } from '../types'

const toPreview = (data?: ConversationMessages): string => {
  const body = data?.conversation_messages?.[0]?.body
  if (!body) {
    return ''
  }
  return body.replace(/\s+/g, ' ').trim()
}

// Fetches the single newest message per popover thread (patient, guardian) for the row preview.
// Fixed call count keeps hook order stable. AI is not part of the popover list.
export const useThreadPreviews = (threads: MessengerThread[]): Partial<Record<MessengerParticipant, string>> => {
  // Previews only render in the multi-thread list; skip the fetches otherwise.
  const enabled = threads.length > 1
  const patientConversationId = enabled
    ? (threads.find((thread) => thread.participant === MessengerParticipant.PATIENT)?.conversationId ?? null)
    : null
  const guardianConversationId = enabled
    ? (threads.find((thread) => thread.participant === MessengerParticipant.GUARDIAN)?.conversationId ?? null)
    : null

  const dispatch = useAppDispatch()
  const conversationsClient = useSelector(getConversationsClient)

  useEffect(() => {
    if (!conversationsClient) {
      return
    }
    const trackedConversationIds = [patientConversationId, guardianConversationId].filter((id): id is string => !!id)
    if (trackedConversationIds.length === 0) {
      return
    }
    const onMessageAdded = (message: { conversation: { sid: string } }) => {
      const conversationId = message?.conversation?.sid
      if (!conversationId || !trackedConversationIds.includes(conversationId)) {
        return
      }
      dispatch(messagesApi.endpoints.getLatestMessage.initiate({ conversationId }, { forceRefetch: true }))
    }
    conversationsClient.on('messageAdded', onMessageAdded)
    return () => {
      conversationsClient.off('messageAdded', onMessageAdded)
    }
  }, [conversationsClient, dispatch, patientConversationId, guardianConversationId])

  const { data: patientData } = useGetLatestMessageQuery(
    patientConversationId ? { conversationId: patientConversationId } : skipToken,
    { refetchOnMountOrArgChange: true },
  )
  const { data: guardianData } = useGetLatestMessageQuery(
    guardianConversationId ? { conversationId: guardianConversationId } : skipToken,
    { refetchOnMountOrArgChange: true },
  )

  return useMemo(
    () => ({
      [MessengerParticipant.PATIENT]: toPreview(patientData),
      [MessengerParticipant.GUARDIAN]: toPreview(guardianData),
    }),
    [patientData, guardianData],
  )
}
